import React, { Suspense, lazy } from 'react';
import { CircularProgress, Box } from '@mui/material';

// Lazy load the entire integrationsSvgIcons module - Single source of truth with lazy loading
// This ensures icons are only loaded when needed, reducing initial bundle size
const loadIconsModule = () => import('utils/icons/integrationsSvgIcons');

// Cache for the loaded icons module
let iconsModuleCache = null;
let iconsModulePromise = null;

// Icon loading fallback component for Suspense
const IconFallback = ({ width = 32, height = 32 }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width, height }}>
    <CircularProgress size={16} />
  </Box>
);

// Create a lazy-loaded icon component factory
const createLazyIcon = (iconName) => {
  return lazy(async () => {
    // Load icons module if not cached
    if (!iconsModuleCache && !iconsModulePromise) {
      iconsModulePromise = loadIconsModule().then(module => {
        iconsModuleCache = module;
        iconsModulePromise = null;
        return module;
      });
    }

    const module = iconsModuleCache || await iconsModulePromise;
    const Icon = module[iconName];

    if (!Icon) {
      // Return a null component if icon not found
      return { default: () => null };
    }

    // Return the icon component wrapped
    return { default: Icon };
  });
};

// Cache for lazy icon components to avoid recreating them
const lazyIconCache = {};

// Wrapper component that uses React.lazy() with Suspense
const LazyIconWrapper = ({ iconName, width = 32, height = 32, ...props }) => {
  if (!iconName) return null;

  // Get or create lazy component for this icon
  if (!lazyIconCache[iconName]) {
    lazyIconCache[iconName] = createLazyIcon(iconName);
  }

  const LazyIcon = lazyIconCache[iconName];

  return (
    <Suspense fallback={<IconFallback width={width} height={height} />}>
      <LazyIcon width={width} height={height} {...props} />
    </Suspense>
  );
};

// Centralized icon name mapping - Single source of truth
const iconNameMappings = {
  // CRM Icons
  'Salesforce': 'Salesforce',
  'Hubspot': 'Hubspot',
  'HubSpot': 'Hubspot', // Handle capital S variant
  'Zoho': 'Zoho',
  'Zoho CRM': 'Zoho', // Handle Zoho CRM variant
  'Dynamics': 'Dynamics',
  'Microsoft Dynamics': 'Dynamics', // Handle full name variant
  'Pardot': 'Pardot',
  'Pipedrive': 'Pipedrive',
  'Monday': 'Monday',
  'Marketo': 'Marketo',
  'Zapier': 'Zapier',
  'VinSolutions': 'VinSolutions',

  // HR Icons
  'Active Directory': 'MicrosoftAzure',
  'Google Workspace': 'Google',
  'Workday': 'Workday',
  'Eventbrite': 'Eventbrite',
  'Slack': 'Slack',

  // Email Icons
  'Outlook Email Signature': 'Outlook',
  'Outlook Contact Sync': 'Outlook',
  'Outlook (Individual)': 'Outlook', // Added Outlook Individual variant
  'Outlook (Full Team)': 'Outlook',

  // SAML Icons
  'Azure SAML': 'AzureSAML',
  'Okta SAML': 'Okta',

  // Calendar Booking Icons
  'Chili Piper': 'ChiliPiper',
};

/**
 * Get icon component by name - Single source of truth with lazy loading
 * All components should use this function instead of direct imports
 * Icons are lazy loaded on-demand to improve initial bundle size
 * @param {string} name - Integration name
 * @returns {React.Component} - Lazy-loaded icon component wrapped in Suspense
 */
export const getIconComponent = (name) => {
  const iconName = iconNameMappings[name];

  if (!iconName) {
    return null;
  }

  // Return a component that lazy loads the icon
  return (props) => <LazyIconWrapper iconName={iconName} {...props} />;
};

// Export icon name mappings for reference (not recommended for direct use, use getIconComponent instead)
export { iconNameMappings };